import React, {
  type DragEventHandler,
  forwardRef,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { tv } from "tailwind-variants";
import { useIsDragging } from "../../../hooks/useIsDragging";
import { cn } from "../../../util/classNameConstructor";
import { getFileIcon } from "../../special/imageModifier/MediaBrowser";
import Icon from "../icon/Icon";
import type { IconName } from "../icon/names";
import { bodySm } from "../typography/Typography";
const FileDropVariant = tv({
  base: "flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-xl p-2",
  slots: {
    label: "relative p-0.5 text-center text-white",
  },
  variants: {
    hidden: {
      false:
        "text-text-subdued [&_svg]:text-icon-subdued bg-surface-subdued hover:border-action-primary-default hover:bg-surface-highlight-subdued hover:text-[var(--primary-600)] hover:text-[var(--primary-600)] [&:hover_svg]:text-[var(--primary-600)] [&:hover_svg]:text-[var(--primary-600)] cursor-pointer",
      true: "!border-transparent",
    },
    drag: {
      true: "border-action-primary-default bg-surface-highlight-subdued border-2",
      false: "border-border-default border-2 border-dashed",
    },
    file: {
      true: "",
    },
  },
});

interface FileDropProps {
  onDrop: (files: FileList | File[]) => void;
  onClick?: () => void;
  text?: string | false;
  className?: string;
  classNames?: {
    image?: string;
    icon?: string;
    text?: string;
  };
  icon?: IconName;
  loading?: boolean;
  showImage?: boolean;
  multiple?: boolean;
  fileTypes?: string[];
  fileTypesText?: string | false;
  hidden?: boolean;
}

const FileDrop = forwardRef<HTMLInputElement, FileDropProps>(
  (
    {
      onDrop,
      onClick,
      className,
      classNames,
      showImage,
      multiple,
      hidden,
      text,
      icon,
      loading,
      fileTypes = ["image/jpg", "image/png", "image/jpeg"],
      fileTypesText,
    },
    forwardedRef,
  ) => {
    const { t } = useTranslation();
    const [drag, setDrag] = useState(false);
    const [files, setFiles] = useState<FileList | File[]>();
    const { base } = FileDropVariant({
      drag: !!drag,
      file: !!files?.length,
      hidden: !!hidden && !drag,
    });

    const strippedFileTypes = fileTypes
      .filter((type) => type !== "image/jpeg")
      .map((type, index, array) => {
        return `.${type.split("/").pop()}`;
      })
      .join(", ");

    const subtext =
      fileTypesText === false ? null : fileTypesText || strippedFileTypes;

    const dropContainer = useRef<HTMLDivElement>(null);
    const defaultInputRef = useRef<HTMLInputElement>(null);
    const inputRef = forwardedRef || defaultInputRef;

    const processDrag = (e: Event | React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrag: EventListenerOrEventListenerObject = (e) => {
      processDrag(e);
    };

    const handleDragIn: DragEventHandler<HTMLDivElement> = (e) => {
      processDrag(e);
      setDrag(true);
    };

    const handleDragOut: DragEventHandler<HTMLDivElement> = (e) => {
      processDrag(e);
      setDrag(false);
    };

    function handleDrop(
      e: DragEvent | React.ChangeEvent<HTMLInputElement>,
      type?: "inputFile",
    ) {
      if (type === "inputFile" && !(e instanceof DragEvent)) {
        const { files } = e.target;
        if (files && files.length > 0) {
          setFiles(files);
          onDrop(files);
          // Reset the value of the input to ensure a change is detected next time
          e.target.value = "";
        }
      } else if (e instanceof DragEvent) {
        const { files } = e.dataTransfer || {};
        processDrag(e);
        setDrag(false);
        if (files && files.length > 0) {
          setFiles(multiple ? files : [files[0]]);
          onDrop(multiple ? files : [files[0]]);
        }
      }
    }

    const isDragging = useIsDragging();
    React.useEffect(() => {
      const div = dropContainer.current;

      div?.addEventListener("dragover", handleDrag);
      div?.addEventListener("drop", handleDrop);
      return () => {
        div?.removeEventListener("dragover", handleDrag);
        div?.removeEventListener("drop", handleDrop);
      };
    }, []);

    const fileIcon = useMemo<IconName>(() => {
      const icons = fileTypes.map((type) => getFileIcon(type, multiple));
      if (icons.length === new Set(icons).size) {
        return icons[0];
      }
      return getFileIcon(undefined, multiple);
    }, [fileTypes, multiple]);

    return (
      // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
      <div
        className={cn(
          base({ className }),
          isDragging || drag
            ? "!border-action-primary-default z-50 rounded-lg"
            : hidden
              ? "-z-50"
              : "",
        )}
        ref={dropContainer}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onClick={() => {
          if (onClick) {
            onClick();
          } else if (!hidden) {
            inputRef.current?.click();
          }
        }}
      >
        {[...(files || [])]?.map((file) =>
          showImage && fileTypes.includes(file.type) ? (
            <img
              key={file.name}
              src={URL.createObjectURL(file)}
              className={cn(classNames?.image)}
              alt={file.name}
            />
          ) : null,
        )}
        {!showImage ||
        ![...(files || [])].some((file) => fileTypes.includes(file.type)) ? (
          <div
            className={cn(
              drag ? "pointer-events-none" : "",
              hidden ? "" : "flex flex-1 flex-col justify-center p-4",
            )}
          >
            {(!hidden || !!icon) && (
              <Icon
                name={loading ? "loading" : icon || fileIcon || "add-image"}
                className={cn(
                  "mx-auto mb-2 grayscale mix-blend-luminosity",
                  classNames?.icon,
                )}
              />
            )}
            {!hidden && text !== false && (
              <div className="flex flex-col items-center justify-center gap-1">
                <span
                  className={cn(
                    "flex flex-col gap-2 text-center",
                    bodySm,
                    classNames?.text,
                  )}
                >
                  {text ||
                    (fileTypes?.every((e) => e.includes("image"))
                      ? multiple
                        ? t("util:dragAndDrop", { val: t("global:images") })
                        : t("util:dragAndDrop", { val: t("global:image") })
                      : fileTypes?.every((e) => e.includes("video"))
                        ? multiple
                          ? t("util:dragAndDrop", { val: t("global:videos") })
                          : t("util:dragAndDrop", { val: t("global:video") })
                        : multiple
                          ? t("util:dragAndDrop", { val: t("global:files") })
                          : t("util:dragAndDrop", { val: t("global:file") }))}
                </span>
                {subtext && (
                  <span className="body-sm font-size-[5px] flex gap-1 whitespace-nowrap text-center">
                    ({subtext})
                  </span>
                )}
              </div>
            )}
            <input
              className="hidden opacity-0"
              type="file"
              accept={fileTypes.join(",")}
              ref={inputRef}
              onChange={(e) => handleDrop(e, "inputFile")}
              multiple={multiple}
            />
          </div>
        ) : null}
      </div>
    );
  },
);

FileDrop.displayName = "FileDrop";

export default FileDrop;
